import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Users, Mail, CheckCircle2 } from "lucide-react";
import { ApiError } from "@finora/api-client";
import { EmptyState, Skeleton, Button } from "@finora/ui";
import { api } from "../../lib/api";

/**
 * Finora Admin -> Korisnici: globalna (cross-company) lista SVIH korisnika
 * platforme, sa mogućnošću ručnog ponovnog slanja mejla za potvrdu naloga.
 * Vidi razgovor "korisnik se registruje, ne dobije mail ili ga slucajno
 * obrise, ne moze da se uloguje niti se vidi u adminu" (24.09.2026).
 *
 * NAMERNO globalna, ne dodata kao tab na CompanyDetailPage: dok korisnik ne
 * potvrdi nalog (postavi lozinku), NEMA nijednu firmu (signup() ne pravi
 * CompanyUser red) - ne bi se pojavio ni na jednoj per-firma listi.
 */
export function PlatformUsersPage() {
  const queryClient = useQueryClient();
  const [onlyUnverified, setOnlyUnverified] = useState(true);
  const [justSentId, setJustSentId] = useState<string | null>(null);

  const { data: users, isLoading } = useQuery({
    queryKey: ["platform-users", onlyUnverified],
    queryFn: () => api.listAllUsers(onlyUnverified),
  });

  const resend = useMutation({
    mutationFn: (userId: string) => api.resendUserVerificationEmail(userId),
    onSuccess: (_data, userId) => {
      setJustSentId(userId);
      queryClient.invalidateQueries({ queryKey: ["platform-users"] });
    },
  });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Korisnici</h1>
        <p className="text-sm text-slate-500">
          Svi korisnici platforme (svih firmi). Nepotvrđen nalog = korisnik nije kliknuo link iz mejla za
          potvrdu (nije postavio lozinku) - ne može da se uloguje i, dok ne prođe onboarding, nema ni firmu.
        </p>
      </div>

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant={onlyUnverified ? "primary" : "secondary"}
          onClick={() => setOnlyUnverified(true)}
        >
          Samo nepotvrđeni
        </Button>
        <Button size="sm" variant={!onlyUnverified ? "primary" : "secondary"} onClick={() => setOnlyUnverified(false)}>
          Svi korisnici
        </Button>
      </div>

      {resend.isError && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {resend.error instanceof ApiError ? resend.error.response.message : "Slanje mejla nije uspelo."}
        </p>
      )}

      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        {isLoading ? (
          <div className="space-y-2 p-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !users || users.length === 0 ? (
          <div className="p-5">
            <EmptyState
              icon={Users}
              title={onlyUnverified ? "Nema nepotvrđenih naloga" : "Nema korisnika"}
              description=""
            />
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Ime i prezime</th>
                <th>Email</th>
                <th>Firma</th>
                <th>Registrovan</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td className="font-medium text-slate-800">{u.fullName}</td>
                  <td className="font-mono text-slate-600">{u.email}</td>
                  <td className="text-slate-500">
                    {u.companyNames.length > 0 ? u.companyNames.join(", ") : "—"}
                  </td>
                  <td className="text-slate-500">{new Date(u.createdAt).toLocaleString("sr-RS")}</td>
                  <td>
                    {u.verified ? (
                      <span className="text-xs text-emerald-600">Potvrđen</span>
                    ) : (
                      <span className="text-xs font-medium text-amber-600">Čeka potvrdu mejla</span>
                    )}
                  </td>
                  <td>
                    {!u.verified &&
                      (justSentId === u.id && !resend.isPending ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                          <CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> Poslato
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={resend.isPending}
                          onClick={() => resend.mutate(u.id)}
                        >
                          <Mail className="h-3.5 w-3.5" aria-hidden />
                          Pošalji verifikacioni mejl
                        </Button>
                      ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
